<template>
  <div class="stats-pdc">
    <div class="card">
      <div class="grid">
        <!-- Header + filtri -->
        <div class="col-12">
          <div class="stats-header">
            <div class="stats-title">
              <i class="pi pi-chart-bar"></i>
              <span>Statistiche PdC</span>
              <i v-if="loading" class="pi pi-spin pi-spinner loading-ic"></i>
            </div>
            <div class="filters">
              <Dropdown v-model="period" :options="periodOptions" optionLabel="label" optionValue="value"
                @change="loadDashboard" />
              <Dropdown v-model="tipo" :options="tipoOptions" optionLabel="label" optionValue="value"
                @change="loadDashboard" />
              <Button icon="pi pi-list" label="Elenco POD" class="p-button-outlined"
                @click="openPodsDialog" :disabled="!!loadError || loading" />
              <Button icon="pi pi-refresh" label="Aggiorna" @click="loadDashboard" :loading="loading" />
            </div>
          </div>
        </div>

        <!-- Stato errore dashboard -->
        <div class="col-12" v-if="loadError">
          <div class="error-state">
            <i class="pi pi-exclamation-circle"></i>
            <div class="error-title">Caricamento non riuscito</div>
            <div class="error-msg">{{ loadError }}</div>
          </div>
        </div>

        <!-- KPI per tipo pratica -->
        <template v-if="!loadError">
        <div class="col-12">
          <p class="page-intro">
            Andamento delle richieste di <strong>sospensione</strong> e <strong>riattivazione</strong> inviate alla PdC nel periodo selezionato, e del loro esito.
            Il numero grande è il totale delle <strong>richieste inviate</strong>; i numeri sotto spiegano com'è andata.
          </p>
        </div>
        <div :class="kpiList.length === 1 ? 'col-12' : 'col-12 md:col-6'" v-for="k in kpiList" :key="k.tipo">
          <div class="kpi" :class="{ 'kpi-wide': kpiList.length === 1 }">
            <div class="kpi-label">
              <i :class="k.icon"></i>
              <span>{{ k.label }}</span>
            </div>
            <div class="kpi-hero">
              <span class="kpi-num">{{ k.inviate }}</span>
              <span class="kpi-unit">richieste inviate</span>
            </div>
            <div class="kpi-breakdown">
              <div class="bd" title="Richieste accettate dal controllo di ammissibilità della PdC"><span class="bd-num pos">{{ k.ammesse }}</span><span class="bd-lbl">Ammesse</span></div>
              <div class="bd" title="Richieste respinte subito dal controllo di ammissibilità"><span class="bd-num neg">{{ k.ko_immediato }}</span><span class="bd-lbl">KO immediato</span></div>
              <div class="bd" title="Pratiche con esito finale positivo (sospensione/riattivazione completata)"><span class="bd-num pos">{{ k.eseguite }}</span><span class="bd-lbl">Eseguite</span></div>
              <div class="bd" title="Pratiche con esito finale negativo"><span class="bd-num neg">{{ k.non_eseguite }}</span><span class="bd-lbl">Non eseguite</span></div>
            </div>
            <div class="kpi-desc">
              <b>Ammesse</b> / <b>KO immediato</b>: esito del controllo di ammissibilità. <b>Eseguite</b> / <b>Non eseguite</b>: esito finale della pratica.
            </div>
          </div>
        </div>
        </template>

        <!-- Ricerca POD -->
        <div class="col-12">
          <div class="panel">
            <div class="panel-title">Ricerca POD</div>
            <p class="section-desc">Cerca un POD per vederne lo stato attuale, lo storico dei passaggi di stato e i flussi PdC.</p>
            <div class="search-row">
              <InputText v-model="podCode" placeholder="Codice POD (es. IT001E12345678)"
                @keyup.enter="cercaPod" />
              <Button label="Cerca" icon="pi pi-search" @click="cercaPod" :loading="podLoading" />
            </div>

            <div v-if="podError" class="banner-error mt-3">
              <i class="pi pi-exclamation-triangle"></i>
              <span>{{ podError }}</span>
            </div>

            <div v-if="podResult" class="pod-result">
              <div class="pod-head">
                <span class="pod-code">{{ podResult.pod.code }}</span>
                <div class="pod-signals">
                  <span class="signal">
                    <span class="dot" :style="{ background: statoColor }"></span>
                    {{ podResult.pod.stato_attuale || statoLabel(podResult.pod.cod_type_state) }}
                  </span>
                  <Button label="Chiudi" icon="pi pi-times" class="p-button-text p-button-sm" @click="chiudiPod" />
                </div>
              </div>

              <div class="pod-grid">
                <div class="pg-item">
                  <span class="pg-lbl">Indirizzo</span>
                  <span class="pg-val">{{ podResult.pod.indirizzo || '-' }}</span>
                </div>
                <div class="pg-item">
                  <span class="pg-lbl">Stati registrati</span>
                  <span class="pg-val">{{ podResult.storico_stati.length }}</span>
                </div>
                <div class="pg-item">
                  <span class="pg-lbl">Flussi PdC</span>
                  <span class="pg-val">{{ podFlussiRows.length }}</span>
                </div>
              </div>

              <div class="sub-title">Storico stati pratica</div>
              <DataTable :value="podResult.storico_stati" :paginator="podResult.storico_stati.length > 10" :rows="10"
                :rowsPerPageOptions="[10, 25, 50]" responsiveLayout="scroll" class="p-datatable-sm">
                <Column header="Data e ora">
                  <template #body="{ data }">{{ data.data || '-' }}</template>
                </Column>
                <Column field="stato_label" header="Stato"></Column>
                <Column header="Origine">
                  <template #body="{ data }">
                    <span class="origine" :class="{ 'origine-op': data.da_operatore }">{{ data.origine }}</span>
                  </template>
                </Column>
                <Column field="motivazione" header="Motivazione"></Column>
                <template #empty>Nessuno stato registrato.</template>
              </DataTable>

              <div class="sub-title">Flussi PdC del POD</div>
              <DataTable :value="podFlussiRows" :paginator="podFlussiRows.length > 10" :rows="10"
                :rowsPerPageOptions="[10, 25, 50]" responsiveLayout="scroll" class="p-datatable-sm">
                <Column field="tipoLabel" header="Tipo"></Column>
                <Column field="sorgente" header="Fase"></Column>
                <Column field="esito" header="Esito"></Column>
                <Column field="cod_causale" header="Causale"></Column>
                <Column field="motivazione" header="Motivazione"></Column>
                <Column field="data" header="Data"></Column>
                <template #empty>Nessun flusso PdC per questo POD.</template>
              </DataTable>
            </div>
          </div>
        </div>

        <!-- Causali di errore -->
        <div class="col-12" v-if="!loadError">
          <div class="panel">
            <div class="panel-title">Causali di errore <span class="muted">· {{ periodLabel.toLowerCase() }}</span></div>
            <p class="section-desc">
              Ogni riga è una <strong>causale di errore</strong> con quante pratiche l'hanno avuta.
              Fase «<strong>Ammissibilità</strong>» = respinta al controllo iniziale; «<strong>Esito</strong>» = respinta nell'esito finale.
              <strong>Clicca una riga</strong> per vedere i POD coinvolti.
            </p>
            <DataTable :value="erroriRows" :paginator="erroriRows.length > 10" :rows="10"
              :rowsPerPageOptions="[10, 25, 50]" responsiveLayout="scroll" :loading="loading"
              sortField="totale" :sortOrder="-1" class="p-datatable-sm errori-table"
              @row-click="openErroreModal">
              <Column field="tipoLabel" header="Tipo" sortable></Column>
              <Column field="fase" header="Fase" sortable></Column>
              <Column field="cod_causale" header="Causale" sortable></Column>
              <Column field="motivazione" header="Motivazione"></Column>
              <Column field="totale" header="Totale" sortable></Column>
              <template #empty>Nessun errore registrato nel periodo.</template>
            </DataTable>
          </div>
        </div>

        <!-- Grafici (menù apri/chiudi) -->
        <div class="col-12" v-if="!loadError">
          <div class="panel">
            <button class="collapse-head" @click="graficiAperti = !graficiAperti" :aria-expanded="graficiAperti">
              <span class="collapse-title">Grafici</span>
              <i class="pi pi-chevron-down chev" :class="{ open: graficiAperti }"></i>
            </button>

            <transition name="collapse">
              <div v-show="graficiAperti" class="collapse-body">
              <!-- Stato pratiche: barra segmentata per fase -->
              <div class="graf-block">
                <div class="panel-title">Stato pratiche <span class="muted">· attuale</span></div>
                <p class="section-desc">Quanti POD si trovano <strong>ora</strong> in ciascuna fase della pratica (foto del momento, non dipende dal periodo).</p>
                <div v-if="statoTotale > 0">
                  <div class="seg-bar">
                    <div v-for="f in statoFasi" :key="f.key" class="seg"
                      :style="{ width: f.pct + '%', background: f.color }"
                      :title="f.label + ': ' + f.totale"></div>
                  </div>
                  <div class="seg-legend">
                    <span v-for="f in statoFasi" :key="f.key" class="seg-leg">
                      <span class="seg-dot" :style="{ background: f.color }"></span>
                      {{ f.label }} <b>{{ f.totale }}</b>
                    </span>
                  </div>
                </div>
                <div v-else class="empty-box seg-empty">
                  <i class="pi pi-inbox"></i>
                  <span>Nessun POD negli stati PdC</span>
                </div>
              </div>

              <!-- Andamento flussi -->
              <div class="graf-block">
                <div class="panel-title">Andamento flussi <span class="muted">· {{ periodLabel.toLowerCase() }}</span></div>
                <p class="section-desc">Numero di richieste per giorno nel periodo: <span class="lg-inviate">inviate</span>, <span class="lg-ammesse">ammesse</span> e <span class="lg-ko">respinte subito</span> (KO immediato).</p>
                <div class="chart-wrap">
                  <Chart v-if="andamentoChart.labels.length" type="line" :data="andamentoChart" :options="lineOptions" />
                  <div v-else class="empty-box">
                    <i class="pi pi-inbox"></i>
                    <span>Nessun dato nel periodo</span>
                  </div>
                </div>
              </div>
            </div>
            </transition>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal dettaglio causale (POD coinvolti) -->
    <Dialog v-model:visible="erroreDialogVisible" modal :dismissableMask="true"
      :header="erroreDialogHeader" :style="{ width: '42rem' }" :breakpoints="{ '640px': '95vw' }">
      <div v-if="erroreSelezionato" class="err-modal">
        <div class="err-meta">
          <div><span class="em-lbl">Tipo</span><span class="em-val">{{ erroreSelezionato.tipoLabel }}</span></div>
          <div><span class="em-lbl">Fase</span><span class="em-val">{{ erroreSelezionato.fase }}</span></div>
          <div><span class="em-lbl">Causale</span><span class="em-val">{{ erroreSelezionato.cod_causale || '—' }}</span></div>
          <div><span class="em-lbl">Pratiche</span><span class="em-val">{{ erroreSelezionato.totale }}</span></div>
        </div>
        <div v-if="erroreSelezionato.motivazione" class="err-motivazione">{{ erroreSelezionato.motivazione }}</div>

        <div class="sub-title">POD coinvolti</div>
        <DataTable :value="erroreSelezionato.pods" :paginator="(erroreSelezionato.pods || []).length > 10" :rows="10"
          :rowsPerPageOptions="[10, 25, 50]" responsiveLayout="scroll" class="p-datatable-sm">
          <Column field="cod_pod" header="POD"></Column>
          <Column field="data" header="Data"></Column>
          <template #empty>Nessun POD.</template>
        </DataTable>
      </div>
    </Dialog>

    <!-- Modal elenco POD richieste inviate (paginazione server-side) -->
    <Dialog v-model:visible="podsDialogVisible" modal :dismissableMask="true"
      :header="podsDialogHeader" :style="{ width: '72rem' }" :breakpoints="{ '960px': '95vw' }"
      class="pods-dialog" @show="onPodsDialogShow" @hide="onPodsDialogHide">
      <p class="section-desc pods-dialog-desc">
        POD con almeno una richiesta inviata alla PdC nel periodo e tipologia selezionati
        ({{ periodLabel }}).
        Usa la ricerca per verificare se un POD è in elenco.
        <strong>Clicca una riga</strong> per aprirne il dettaglio nella ricerca in pagina.
      </p>
      <div v-if="podsError" class="banner-error pods-dialog-error">
        <i class="pi pi-exclamation-circle"></i>
        <span>{{ podsError }}</span>
      </div>
      <div class="pods-dialog-search">
        <span class="p-input-icon-left pods-search-wrap">
          <i class="pi pi-search"></i>
          <InputText
            v-model="podsSearch"
            placeholder="Cerca POD (es. IT001E12345678)"
            class="pods-search-input"
            @keydown.enter.prevent="onPodsSearchSubmit"
          />
        </span>
        <span class="pods-search-meta" v-if="!podsLoading">
          <template v-if="podsTotale">
            {{ podsTotale }} POD · pag. {{ podsPage }}/{{ podsPages || 1 }}
          </template>
          <template v-else-if="podsSearchTrimmed">
            Nessun POD trovato
          </template>
          <template v-else>
            Nessun POD in {{ periodLabel.toLowerCase() }}
          </template>
        </span>
      </div>
      <DataTable
        :value="podsRows"
        :lazy="true"
        :paginator="true"
        :rows="podsLimit"
        :first="podsFirst"
        :totalRecords="podsTotale"
        :rowsPerPageOptions="[25, 50, 100, 200]"
        :loading="podsLoading"
        responsiveLayout="scroll"
        scrollable
        scrollHeight="28rem"
        class="p-datatable-sm pods-inviate-table"
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="{first}–{last} di {totalRecords}"
        @page="onPodsPage"
        @row-click="openPodFromList"
      >
        <Column field="cod_pod" header="POD"></Column>
        <Column field="tipoLabel" header="Tipo"></Column>
        <Column field="n_richieste" header="Richieste"></Column>
        <Column field="data_ultima" header="Ultima richiesta">
          <template #body="{ data }">{{ formatData(data.data_ultima) }}</template>
        </Column>
        <template #empty>
          <template v-if="podsLoading">Caricamento…</template>
          <template v-else-if="podsSearchTrimmed">Nessun POD corrisponde alla ricerca.</template>
          <template v-else>Nessun POD con richieste inviate nel periodo «{{ periodLabel }}».</template>
        </template>
      </DataTable>
    </Dialog>
  </div>
</template>

<script>
import GestionePdcStatsService from '@/services/ask-pratiche/gestione-pdc-stats';

const TIPO_META = {
  sospensioni: { label: 'Sospensioni · SM', icon: 'pi pi-pause-circle' },
  riattivazioni: { label: 'Riattivazioni · RT', icon: 'pi pi-play-circle' },
};

// Aggregazione degli stati POD nelle 4 fasi della pratica.
// Codici da AdminPDCSenderController: SM richiesta 21 / lavorazione 22 / ko-temp 28 / ko-def 25;
// RT richiesta 23 / lavorazione 24 / ko-temp 29 / ko-def 26.
const FASI = [
  { key: 'richieste', label: 'Richieste', color: '#2196f3', stati: [21, 23] },
  { key: 'lavorazione', label: 'In lavorazione', color: '#ef9f27', stati: [22, 24] },
  { key: 'ko_temp', label: 'KO temporaneo', color: '#ef7d3b', stati: [28, 29] },
  { key: 'ko_def', label: 'KO definitivo', color: '#e24b4a', stati: [25, 26] },
];

// Etichette testuali degli stati POD (codice numerico → testo).
const STATI_LABEL = {
  3: 'Cessato',
  18: 'Sospeso',
  19: 'Riattivato',
  20: 'Riattivato (spostamento)',
  21: 'Sospensione richiesta',
  22: 'Sospensione in lavorazione',
  23: 'Riattivazione richiesta',
  24: 'Riattivazione in lavorazione',
  25: 'Sospensione KO definitivo',
  26: 'Riattivazione KO definitivo',
  27: 'Sospensione costi',
  28: 'Sospensione KO temporaneo',
  29: 'Riattivazione KO temporaneo',
};

export default {
  name: 'StatistichePDC',
  data() {
    return {
      loading: false,
      loadError: null,
      period: 'settimana',
      tipo: 'tutti',
      graficiAperti: true,
      periodOptions: [
        { label: 'Oggi', value: 'oggi' },
        { label: 'Ultima settimana', value: 'settimana' },
        { label: 'Ultimo mese', value: 'mese' },
      ],
      tipoOptions: [
        { label: 'Tutte', value: 'tutti' },
        { label: 'Sospensioni', value: 'sospensioni' },
        { label: 'Riattivazioni', value: 'riattivazioni' },
      ],
      kpiList: [],
      andamentoChart: { labels: [], datasets: [] },
      statoCorrente: [],
      erroriRows: [],
      podsRows: [],
      podsTotale: 0,
      podsPages: 0,
      podsPage: 1,
      podsLimit: 50,
      podsFirst: 0,
      podsLoading: false,
      podsError: null,
      podsDialogVisible: false,
      podsSearch: '',
      podsSearchTimer: null,
      podsRequestId: 0,
      lineOptions: {
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: { legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8 } } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
      },
      podCode: '',
      podLoading: false,
      podError: null,
      podResult: null,
      erroreDialogVisible: false,
      erroreSelezionato: null,
    };
  },
  computed: {
    periodLabel() {
      const found = this.periodOptions.find((o) => o.value === this.period);
      return found ? found.label : this.period;
    },
    erroreDialogHeader() {
      if (!this.erroreSelezionato) return 'Dettaglio errore';
      return 'Causale: ' + (this.erroreSelezionato.cod_causale || '—');
    },
    podsDialogHeader() {
      const tot = Number(this.podsTotale || 0);
      const totLabel = this.podsLoading && !tot ? '…' : String(tot);
      return `POD richieste inviate · ${this.periodLabel} · ${totLabel}`;
    },
    podsSearchTrimmed() {
      return String(this.podsSearch || '').trim().toUpperCase();
    },
    statoFasi() {
      const byStato = {};
      (this.statoCorrente || []).forEach((s) => {
        byStato[Number(s.stato)] = Number(s.totale || 0);
      });
      const fasi = FASI.map((f) => ({
        ...f,
        totale: f.stati.reduce((acc, code) => acc + (byStato[code] || 0), 0),
      }));
      const tot = fasi.reduce((a, f) => a + f.totale, 0);
      return fasi.map((f) => ({ ...f, pct: tot > 0 ? (f.totale / tot) * 100 : 0 }));
    },
    statoTotale() {
      return this.statoFasi.reduce((a, f) => a + f.totale, 0);
    },
    statoColor() {
      const s = ((this.podResult && this.podResult.pod && this.podResult.pod.stato_attuale) || '').toLowerCase();
      if (!s) return '#9e9e9e';
      if (s.includes('ko') || s.includes('annull') || s.includes('rifiut') || s.includes('scart')) return '#e24b4a';
      if (s.includes('sosp')) return '#d4870f';
      if (s.includes('riatt') || s.includes('attiv') || s.includes('esegu') || s.includes('complet')) return '#1ea97c';
      if (s.includes('lavor') || s.includes('richiest') || s.includes('invio') || s.includes('attesa') || s.includes('corso')) return '#2196f3';
      return '#9e9e9e';
    },
    podFlussiRows() {
      if (!this.podResult || !this.podResult.flussi) return [];
      const rows = [];
      Object.entries(this.podResult.flussi).forEach(([tipo, blocco]) => {
        (blocco.out || []).forEach((r) => {
          rows.push({
            tipoLabel: (TIPO_META[tipo] && TIPO_META[tipo].label) || tipo,
            sorgente: 'Richiesta',
            esito: r.VERIFICA_AMM === '1' ? 'Ammessa' : 'KO',
            cod_causale: r.COD_CAUSALE,
            motivazione: r.MOTIVAZIONE,
            data: r.created_at,
          });
        });
        (blocco.async || []).forEach((r) => {
          rows.push({
            tipoLabel: (TIPO_META[tipo] && TIPO_META[tipo].label) || tipo,
            sorgente: 'Esito',
            esito: r.ESITO,
            cod_causale: r.COD_CAUSALE,
            motivazione: r.MOTIVAZIONE,
            data: r.DATA_ESECUZIONE || r.created_at,
          });
        });
      });
      return rows;
    },
  },
  created() {
    this.service = new GestionePdcStatsService();
    this.loadDashboard();
  },
  beforeUnmount() {
    if (this.podsSearchTimer) clearTimeout(this.podsSearchTimer);
  },
  watch: {
    podsSearch() {
      if (!this.podsDialogVisible) return;
      if (this.podsSearchTimer) clearTimeout(this.podsSearchTimer);
      this.podsSearchTimer = setTimeout(() => {
        this.podsPage = 1;
        this.podsFirst = 0;
        this.loadPodsPage();
      }, 350);
    },
  },
  methods: {
    async loadDashboard() {
      this.loading = true;
      this.loadError = null;
      this.resetPodsList();
      try {
        const params = { period: this.period, tipo: this.tipo };
        const [flussiRes, statoRes, erroriRes] = await Promise.all([
          this.service.getFlussi(params),
          this.service.getStatoPratiche(params),
          this.service.getErrori(params),
        ]);

        this.buildKpi(flussiRes.flussi || {});
        this.buildAndamentoChart(flussiRes.flussi || {});
        this.statoCorrente = (statoRes.stato_pratiche && statoRes.stato_pratiche.corrente) || [];
        this.buildErrori(erroriRes.errori || {});
        if (this.podsDialogVisible) {
          this.loadPodsPage();
        }
      } catch (e) {
        console.error('Errore caricamento statistiche PdC:', e);
        this.loadError = 'Impossibile caricare le statistiche. Riprova con "Aggiorna".';
        this.kpiList = [];
        this.andamentoChart = { labels: [], datasets: [] };
        this.statoCorrente = [];
        this.erroriRows = [];
        this.resetPodsList();
      } finally {
        this.loading = false;
      }
    },

    resetPodsList() {
      this.podsRows = [];
      this.podsTotale = 0;
      this.podsPages = 0;
      this.podsPage = 1;
      this.podsFirst = 0;
      this.podsError = null;
    },

    async loadPodsPage() {
      const requestId = ++this.podsRequestId;
      this.podsLoading = true;
      this.podsError = null;
      try {
        const params = {
          period: this.period,
          tipo: this.tipo,
          page: this.podsPage,
          limit: this.podsLimit,
        };
        const q = this.podsSearchTrimmed;
        if (q) params.q = q;

        const podsRes = await this.service.getPods(params);
        if (requestId !== this.podsRequestId) return;

        this.podsRows = Array.isArray(podsRes.pods) ? podsRes.pods : [];
        this.podsTotale = Number(podsRes.totale || 0);
        this.podsPages = Number(podsRes.pages || 0);
        this.podsPage = Number(podsRes.page || this.podsPage);
        this.podsLimit = Number(podsRes.limit || this.podsLimit);
        this.podsFirst = (this.podsPage - 1) * this.podsLimit;
      } catch (e) {
        if (requestId !== this.podsRequestId) return;
        console.error('Errore caricamento elenco POD:', e);
        this.podsRows = [];
        this.podsTotale = 0;
        this.podsPages = 0;
        this.podsError = 'Impossibile caricare l\'elenco POD. Riprova.';
      } finally {
        if (requestId === this.podsRequestId) {
          this.podsLoading = false;
        }
      }
    },

    buildKpi(flussiByTipo) {
      this.kpiList = Object.entries(flussiByTipo).map(([tipo, f]) => ({
        tipo,
        label: (TIPO_META[tipo] && TIPO_META[tipo].label) || tipo,
        icon: (TIPO_META[tipo] && TIPO_META[tipo].icon) || 'pi pi-circle',
        inviate: Number(f.kpi.inviate || 0),
        ammesse: Number(f.kpi.ammesse || 0),
        ko_immediato: Number(f.kpi.ko_immediato || 0),
        eseguite: Number(f.kpi.eseguite || 0),
        non_eseguite: Number(f.kpi.non_eseguite || 0),
      }));
    },

    buildAndamentoChart(flussiByTipo) {
      const dateSet = new Set();
      Object.values(flussiByTipo).forEach((f) =>
        (f.serie || []).forEach((r) => dateSet.add(r.data))
      );
      const labels = Array.from(dateSet).sort();

      const sumByDate = (field) =>
        labels.map((d) => {
          let tot = 0;
          Object.values(flussiByTipo).forEach((f) => {
            const row = (f.serie || []).find((r) => r.data === d);
            if (row) tot += Number(row[field] || 0);
          });
          return tot;
        });

      this.andamentoChart = {
        labels,
        datasets: [
          { label: 'Inviate', data: sumByDate('inviate'), borderColor: '#2196f3', backgroundColor: '#2196f3', fill: false, tension: 0.3 },
          { label: 'Ammesse', data: sumByDate('ammesse'), borderColor: '#1ea97c', backgroundColor: '#1ea97c', fill: false, tension: 0.3 },
          { label: 'KO immediato', data: sumByDate('ko_immediato'), borderColor: '#e24b4a', backgroundColor: '#e24b4a', fill: false, tension: 0.3 },
        ],
      };
    },

    buildErrori(erroriByTipo) {
      const rows = [];
      Object.entries(erroriByTipo).forEach(([tipo, e]) => {
        const label = (TIPO_META[tipo] && TIPO_META[tipo].label) || tipo;
        (e.immediati || []).forEach((r) =>
          rows.push({ tipo, tipoLabel: label, fase: 'Ammissibilità', cod_causale: r.cod_causale, motivazione: r.motivazione, totale: Number(r.totale || 0), pods: r.pods || [] })
        );
        (e.finali || []).forEach((r) =>
          rows.push({ tipo, tipoLabel: label, fase: 'Esito', cod_causale: r.cod_causale, motivazione: r.motivazione, totale: Number(r.totale || 0), pods: r.pods || [] })
        );
      });
      rows.sort((a, b) => b.totale - a.totale);
      this.erroriRows = rows;
    },

    openErroreModal(event) {
      this.erroreSelezionato = (event && event.data) || null;
      this.erroreDialogVisible = !!this.erroreSelezionato;
    },

    openPodsDialog() {
      this.podsPage = 1;
      this.podsFirst = 0;
      this.podsDialogVisible = true;
    },

    onPodsDialogShow() {
      this.loadPodsPage();
    },

    onPodsDialogHide() {
      if (this.podsSearchTimer) {
        clearTimeout(this.podsSearchTimer);
        this.podsSearchTimer = null;
      }
      this.podsSearch = '';
      this.podsError = null;
    },

    onPodsPage(event) {
      const nextPage = (event && typeof event.page === 'number') ? event.page + 1 : 1;
      const nextLimit = (event && event.rows) ? event.rows : this.podsLimit;
      const nextFirst = (event && typeof event.first === 'number') ? event.first : 0;
      if (nextPage === this.podsPage && nextLimit === this.podsLimit && this.podsRows.length) {
        return;
      }
      this.podsLimit = nextLimit;
      this.podsFirst = nextFirst;
      this.podsPage = nextPage;
      this.loadPodsPage();
    },

    onPodsSearchSubmit() {
      if (this.podsSearchTimer) {
        clearTimeout(this.podsSearchTimer);
        this.podsSearchTimer = null;
      }
      this.podsPage = 1;
      this.podsFirst = 0;
      this.loadPodsPage();
    },

    openPodFromList(event) {
      const row = event && event.data;
      if (!row || !row.cod_pod) return;
      this.podsDialogVisible = false;
      this.podCode = String(row.cod_pod).trim().toUpperCase();
      this.cercaPod();
    },

    formatData(value) {
      if (!value) return '-';
      const s = String(value);
      return s.length >= 16 ? s.slice(0, 16) : s;
    },

    async cercaPod() {
      const code = (this.podCode || '').trim().toUpperCase();
      if (!code) return;
      this.podLoading = true;
      this.podError = null;
      this.podResult = null;
      try {
        this.podResult = await this.service.getPod(code);
      } catch (e) {
        this.podError =
          e.response && e.response.status === 404
            ? 'POD non trovato.'
            : 'Errore durante la ricerca del POD.';
      } finally {
        this.podLoading = false;
      }
    },

    chiudiPod() {
      this.podResult = null;
      this.podError = null;
      this.podCode = '';
    },

    statoLabel(stato, fallback) {
      const n = Number(stato);
      if (STATI_LABEL[n]) return STATI_LABEL[n];
      if (fallback) return fallback;
      return stato !== null && stato !== undefined && stato !== '' ? 'Stato ' + stato : '-';
    },
  },
};
</script>

<style scoped>
.stats-pdc :deep(.p-dropdown) {
  min-width: 12rem;
}

/* Header */
.stats-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid var(--surface-border);
}
.stats-title {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.4rem;
  font-weight: 600;
}
.stats-title .pi-chart-bar {
  font-size: 1.4rem;
  color: var(--text-color-secondary);
}
.loading-ic {
  font-size: 1rem;
  color: var(--text-color-secondary);
}
.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
}

/* KPI */
.kpi {
  background: var(--surface-100);
  border-radius: 12px;
  padding: 1.35rem 1.5rem;
  height: 100%;
}
.kpi-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
  margin-bottom: 0.9rem;
}
.kpi-hero {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
}
.kpi-num {
  font-size: 2.6rem;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: var(--text-color);
}
.kpi-unit {
  font-size: 0.9rem;
  color: var(--text-color-secondary);
}
.kpi-breakdown {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 1.1rem;
  padding-top: 0.9rem;
  border-top: 1px solid var(--surface-border);
}
.bd {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}
.bd-num {
  font-size: 1.2rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.bd-lbl {
  font-size: 0.8rem;
  color: var(--text-color-secondary);
}
.pos { color: #1ea97c; }
.neg { color: #d4503e; }

.kpi-wide {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.25rem 3rem;
}
.kpi-wide .kpi-label {
  margin-bottom: 0;
}
.kpi-wide .kpi-breakdown {
  border-top: none;
  padding-top: 0;
  margin-left: auto;
}

/* Pannelli */
.panel {
  border: 1px solid var(--surface-border);
  border-radius: 12px;
  padding: 1.35rem 1.5rem;
  height: 100%;
}
.panel-title {
  font-size: 1.05rem;
  font-weight: 600;
  margin-bottom: 1.1rem;
}
.muted {
  color: var(--text-color-secondary);
  font-weight: 400;
}
.page-intro {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 0.95rem;
  line-height: 1.5;
}
.section-desc {
  margin: -0.6rem 0 1.1rem;
  color: var(--text-color-secondary);
  font-size: 0.88rem;
  line-height: 1.45;
}
.kpi-desc {
  margin-top: 0.9rem;
  font-size: 0.8rem;
  color: var(--text-color-secondary);
  line-height: 1.45;
}
.lg-inviate { color: #2196f3; font-weight: 600; }
.lg-ammesse { color: #1ea97c; font-weight: 600; }
.lg-ko { color: #e24b4a; font-weight: 600; }

/* Tabella errori / POD inviate cliccabili */
.errori-table :deep(.p-datatable-tbody > tr),
.pods-inviate-table :deep(.p-datatable-tbody > tr) {
  cursor: pointer;
}
.pods-dialog-desc {
  margin: 0 0 1rem;
}
.pods-dialog-search {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1.25rem;
  margin-bottom: 1rem;
}
.pods-search-wrap {
  flex: 1 1 18rem;
  display: block;
  position: relative;
}
.pods-search-wrap .pi-search {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color-secondary);
  z-index: 1;
}
.pods-search-input {
  width: 100%;
  padding-left: 2.25rem;
}
.pods-search-meta {
  font-size: 0.88rem;
  color: var(--text-color-secondary);
  white-space: nowrap;
}
.err-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem 2.5rem;
  margin-bottom: 1rem;
}
.err-meta > div {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.em-lbl {
  font-size: 0.78rem;
  color: var(--text-color-secondary);
}
.em-val {
  font-weight: 600;
  color: var(--text-color);
}
.err-motivazione {
  background: var(--surface-100);
  border-radius: 8px;
  padding: 0.6rem 0.85rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: var(--text-color);
}

/* Menù grafici apri/chiudi */
.collapse-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-color);
}
.collapse-title {
  font-size: 1.05rem;
  font-weight: 600;
}
.collapse-head .pi {
  color: var(--text-color-secondary);
}
.collapse-body {
  margin-top: 1.5rem;
}
.collapse-enter-active,
.collapse-leave-active {
  overflow: hidden;
  transition: max-height 0.32s ease, opacity 0.32s ease, margin-top 0.32s ease;
}
.collapse-enter-from,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
  margin-top: 0;
}
.collapse-enter-to,
.collapse-leave-from {
  max-height: 900px;
  opacity: 1;
}
.chev {
  transition: transform 0.3s ease;
}
.chev.open {
  transform: rotate(180deg);
}
.graf-block + .graf-block {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--surface-border);
}

/* Stato pratiche: barra segmentata */
.seg-bar {
  display: flex;
  height: 18px;
  border-radius: 9px;
  overflow: hidden;
  background: var(--surface-200);
}
.seg {
  height: 100%;
  transition: width 0.3s ease;
}
.seg-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem 1.75rem;
  margin-top: 1.1rem;
}
.seg-leg {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.92rem;
  color: var(--text-color);
}
.seg-dot {
  width: 11px;
  height: 11px;
  border-radius: 3px;
}
.seg-leg b {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.seg-empty {
  height: 6rem;
}

/* Grafico andamento */
.chart-wrap {
  position: relative;
  height: 20rem;
}
.chart-wrap :deep(.p-chart) {
  position: relative;
  height: 100%;
  width: 100%;
}
.chart-wrap :deep(canvas) {
  width: 100% !important;
  height: 100% !important;
}
.empty-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 100%;
  color: var(--text-color-secondary);
}
.empty-box .pi {
  font-size: 1.8rem;
  opacity: 0.6;
}

/* Ricerca POD */
.search-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}
.search-row :deep(.p-inputtext) {
  flex: 1 1 18rem;
}
.pod-result {
  margin-top: 1.5rem;
}
.pod-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--surface-border);
}
.pod-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--text-color);
}
.pod-signals {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}
.signal {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.95rem;
  color: var(--text-color);
}
.signal .dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
}
.pod-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem 3rem;
  padding: 1.1rem 0 0.25rem;
}
.pg-item {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
.pg-lbl {
  font-size: 0.8rem;
  color: var(--text-color-secondary);
}
.pg-val {
  font-weight: 600;
  color: var(--text-color);
}
.sub-title {
  font-size: 0.95rem;
  font-weight: 600;
  margin: 1.5rem 0 0.75rem;
}

/* Stato errore dashboard (full-width, centrato) */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.85rem;
  padding: 4rem 2rem;
  text-align: center;
  border: 1px solid var(--surface-border);
  border-radius: 12px;
}
.error-state .pi-exclamation-circle {
  font-size: 2.8rem;
  color: var(--red-400, #f87171);
}
.error-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--text-color);
}
.error-msg {
  font-size: 0.95rem;
  color: var(--text-color-secondary);
  max-width: 28rem;
}

/* Banner errore inline (ricerca POD) */
.banner-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  background: var(--red-50, #fef2f2);
  color: var(--red-700, #b42318);
  border: 1px solid var(--red-200, #fecaca);
}

/* Storico stati: l'operatore risalta rispetto alle automazioni */
.origine {
  color: var(--text-color-secondary);
}
.origine-op {
  color: var(--text-color);
  font-weight: 600;
}
</style>
